/**
 * Scope card — labour rates the tech typed.
 *
 * The scope chat's model writes ONE labour rate, at the top of the scope
 * document (its spec gives options[] and sections[] hours only). A rate sitting
 * on an option or a section can still come from three places: the tech typed it
 * in the card, the card copied the top-level rate down while another field was
 * edited, or the model wrote a number nobody asked for (one doc carried 305, 280
 * and 200 at once). Only the first may price that option or section at Convert.
 *
 * So the card stamps `labour_rate_by_tech: true` beside a rate the tech types,
 * and Convert (api/quote-brain.js handleDraftFromScope) prices an option or a
 * section at its own rate only when the stamp is there. Measured 2026-10-10 on
 * the 60 multi-option scope docs in production: 59 of 144 options carried a
 * rate, 56 of them a copy of the top-level one — which is why "the option has a
 * rate" could not be the test.
 *
 * The stamp is the card's, never the model's. The model re-emits the whole
 * document every turn, so carryTypedRates runs on every reply: it drops any
 * stamp the reply carries and puts the tech's typed rates back.
 *
 * No imports: the card and the node tests both load this file.
 */

export const RATE_BY_TECH = "labour_rate_by_tech";

// The stamp alone. True while the tech is still typing, when the value may be
// "" or half a number — use typedRateOf for the rate itself.
export function isTypedRate(unit) {
  return !!unit && typeof unit === "object" && unit[RATE_BY_TECH] === true;
}

// The rate the tech typed on this option / section / doc, or 0 when there is none.
export function typedRateOf(unit) {
  const n = isTypedRate(unit) ? Number(unit.labour_rate) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

const labelKey = (u) => String((u && u.label) || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const unstamp = (u) => {
  if (!u || typeof u !== "object" || !(RATE_BY_TECH in u)) return u;
  const out = { ...u };
  delete out[RATE_BY_TECH];
  return out;
};
const stamp = (u, rate) => ({ ...u, labour_rate: rate, [RATE_BY_TECH]: true });
const recommendedIndex = (list) => {
  const i = list.findIndex((o) => o && o.recommended);
  return i < 0 ? 0 : i;
};
// Same test the card and Convert use: sections win, and it takes two to be a split.
const isMulti = (d) => !!d && Array.isArray(d.options) && d.options.length >= 2 && !(Array.isArray(d.sections) && d.sections.length >= 2);

// Options or sections. A typed rate follows its unit by LABEL. A renamed unit is
// found by position, but only when the list is the same length and the unit now
// in that position is not one of the other old ones — so a reorder, an insert or
// a delete can lose a typed rate (the box then shows the job's rate again) but
// never hands it to a different option. `from[i]` is the old index new unit i
// took its typed rate from, -1 for none.
function carryUnits(prevList, nextList) {
  if (!Array.isArray(nextList)) return { list: nextList, from: [] };
  const list = nextList.map(unstamp);
  const from = list.map(() => -1);
  if (!Array.isArray(prevList)) return { list, from };
  const prevKeys = prevList.map(labelKey);
  const nextKeys = list.map(labelKey);
  prevList.forEach((p, pi) => {
    const rate = typedRateOf(p);
    if (!rate) return;
    let ni = prevKeys[pi] ? nextKeys.findIndex((k, i) => k === prevKeys[pi] && from[i] < 0) : -1;
    if (ni < 0 && prevList.length === list.length && from[pi] < 0
      && !prevKeys.some((k, i) => i !== pi && k && k === nextKeys[pi])) ni = pi;
    if (ni < 0 || !list[ni] || typeof list[ni] !== "object") return;
    list[ni] = stamp(list[ni], rate);
    from[ni] = pi;
  });
  return { list, from };
}

/**
 * After a model reply: prevDoc is the document the model was sent (with the
 * tech's hand edits), nextDoc the one it wrote back. Returns nextDoc with the
 * tech's typed rates restored.
 *
 *  - A section or a non-recommended option keeps its typed rate whatever the
 *    model wrote there. The tech changes it by typing in the box again, or
 *    clears the box to go back to the job's rate.
 *  - The top-level rate is different: it is the model's to write (the tech can
 *    ask the chat for another rate), so there the model's number stands, as it
 *    always has. A typed top-level rate keeps its stamp only while the model
 *    leaves the number alone, and is put back if the model drops the field.
 *  - With options, the top level mirrors the recommended one. A typed option the
 *    model has now made the recommended one takes its typed rate up with it.
 *
 * NOT for hand edits: a rate the tech has just cleared or retyped would be
 * overwritten with the old one.
 */
export function carryTypedRates(prevDoc, nextDoc) {
  if (!nextDoc || typeof nextDoc !== "object" || Array.isArray(nextDoc)) return nextDoc;
  const prev = prevDoc && typeof prevDoc === "object" ? prevDoc : {};
  let out = unstamp(nextDoc);
  const secs = carryUnits(prev.sections, out.sections);
  const opts = carryUnits(prev.options, out.options);
  if (Array.isArray(out.sections)) out = { ...out, sections: secs.list };
  if (Array.isArray(out.options)) out = { ...out, options: opts.list };
  const moved = Number(out.labour_rate) > 0 && Number(out.labour_rate) !== Number(prev.labour_rate);

  if (isMulti(out)) {
    const ri = recommendedIndex(out.options);
    const rate = typedRateOf(out.options[ri]);
    if (!rate) return out;
    const sameRecommended = isMulti(prev) && opts.from[ri] === recommendedIndex(prev.options);
    if (sameRecommended && moved) {
      // The model moved the recommended option's rate: it is the chat's rate now.
      const options = out.options.slice();
      options[ri] = { ...unstamp(options[ri]), labour_rate: out.labour_rate };
      return { ...out, options };
    }
    return stamp(out, rate);
  }
  const top = typedRateOf(prev);
  return top && !moved ? stamp(out, top) : out;
}
